import { emptyStringToNull, emptyStringToUndefined } from '@openx/components-form/emptyStringToNullable';
import { getFrequencyCappingValidationSchema } from '@openx/components-form/frequencyCappingValidation';
import { isWithinBigIntMax } from '@openx/components-form/isWithinBigIntMax';
import { FLOAT_NUMBER_WITH_MAX_THREE_DECIMALS } from '@openx/components-form/validationRegexes';
import {
  type DealParticipant,
  type DealState,
  dealFormField,
  pmpDealType as pmpDealTypes,
  rboConfigField,
  rboConfigMode,
} from '@openx/types/deal';
import { BUDGET_MODE, BUDGET_TYPE, PACING_MODE } from '@openx/types/monetizing';
import { rboConfigModelGoal, rboConfigTactic } from '@openx/types/rbo';
import type { I18nTranslate } from '@openx/types/translations';
import {
  AMAZON_DEMAND_PARTNER_ID,
  DEAL_SOURCE_DSP_SOURCED,
  DV360_DEMAND_PARTNER_ID,
  TTD_DEMAND_PARTNER_ID,
} from '@openx/utils/services/config';
import { fromZonedTime } from 'date-fns-tz';
import { array, boolean, date, lazy, mixed, number, object, string, type TestOptions } from 'yup';
import { DealParticipantsField } from '../components/DealParticipants/fields';

export const MAX_SAFE_INTEGER_ROUNDED = 9000000000000000;
export const MAX_MONETIZING_BUDGET = 9e12;
export const MAX_MONETIZING_IMPRESSIONS = 9e18;

// Amazon budget is stored and sent as a string. Validate the raw string so signed values,
// scientific notation, whitespace, thousands separators and leading zeros are rejected.
const AMAZON_BUDGET_NUMBER_REGEXP = /^(0|[1-9]\d*)(\.\d+)?$/;

function getEnableDV360InventoryPackageValue(options) {
  return options.some(item => item.value[dealFormField.ENABLE_DV360_INVENTORY_PACKAGE]);
}

function packageValidation() {
  return string().when(dealFormField.TARGETING, {
    is: (targeting: unknown) => !!targeting,
    then: schema => schema.nullable(),
    otherwise: schema => schema.required('Package is required'),
  });
}

/**
 * Returns true when the form represents a PDP Sync deal:
 * Programmatic Guaranteed deal type AND a TTD participant is present.
 *
 * Called inside Yup `.test()` — pass `(this as TestOptions['options']).from`.
 * `from[0]` is the pdp_config context; `from[1]` is the root form context.
 */
function isPdpSyncDeal(from: TestOptions['options']['from']): boolean {
  const rootForm = from?.[1]?.value;
  const isPG = rootForm?.[dealFormField.PMP_DEAL_TYPE] === pmpDealTypes.Guaranteed;
  const hasTTDParticipant = (rootForm?.[DealParticipantsField.DEAL_PARTICIPANTS] ?? []).some(
    (participant: { demand_partner?: string }) => participant.demand_partner === TTD_DEMAND_PARTNER_ID
  );

  return isPG && hasTTDParticipant;
}

export const getDealValidation = (
  t: I18nTranslate,
  isEdit: boolean,
  initialDealState?: DealState,
  options?: {
    enforceMediaSpendGoalFloor?: boolean;
    isCommitmentTermsEnabled?: boolean;
    withAmazonBudget?: boolean;
  }
) => ({
  [dealFormField.NAME]: string().required(t('Name is required')),
  [dealFormField.PMP_DEAL_TYPE]: string().nullable().required(t('Deal Type is required')),
  [dealFormField.CURRENCY]: string().nullable().required(t('Currency is required')),
  [dealFormField.ENABLE_DV360_INVENTORY_PACKAGE]: boolean(),

  [DealParticipantsField.DEAL_PARTICIPANTS]: array()
    .required(t('DSPs is required'))
    .of(
      object().shape({
        [DealParticipantsField.MAIN_BUYER]: string().nullable(),
        [DealParticipantsField.DSP]: string().nullable().required(t('DSPs is required')),
        [DealParticipantsField.BUYER_IDS]: array()
          .test(
            'arrayOfNulls',
            t(
              "Your organization doesn't have access to this seat or it was deactivated. Please contact OpenX if you require access or choose an available seat."
            ),
            buyers => {
              return !(buyers || []).some(buyer => buyer === null);
            }
          )
          .test('required-for-amazon-sync', t('DSP Seat IDs are required'), function (buyers) {
            const { parent } = this as TestOptions['options'];

            if (parent[DealParticipantsField.DSP] !== AMAZON_DEMAND_PARTNER_ID) return true;

            if (parent[DealParticipantsField.AMAZON_MARKETPLACE_ENABLED]) return true;

            return (buyers || []).length > 0;
          }),
        [DealParticipantsField.BUYER_ID]: string()
          .nullable()
          .test('Verify seat id', t('DSP Seat ID is required'), function (buyerIdValue) {
            const { from, parent } = this as TestOptions['options'];

            if (parent.demand_partner === DV360_DEMAND_PARTNER_ID) {
              const isEnableDV360 = getEnableDV360InventoryPackageValue(from);
              if (isEnableDV360 || buyerIdValue) {
                return true;
              }
              return !!buyerIdValue && isEnableDV360;
            }
            return true;
          }),
        [DealParticipantsField.BRAND_IDS]: array().test(
          'arrayOfNulls',
          t('There are some unavailable Brands on a list. Remove them before continuing.'),
          brands => {
            return !(brands || []).some(buyer => buyer === null);
          }
        ),
      })
    ),
  [dealFormField.DEAL_PRICE]: number()
    .nullable()
    .transform(emptyStringToNull)
    .typeError(t('Price has to be a number'))
    .positive(t('Price has to be bigger than 0'))
    .test('maxDigitsAfterDecimal', t('Price can have up to 2 decimal places.'), number => {
      return !number || (number.toString().match(/^\d+(\.\d{0,2})?$/i) || []).length > 0;
    }),
  [dealFormField.PDP_CONFIG]: object({
    media_spend_goal: number()
      .nullable()
      .transform(emptyStringToNull)
      .typeError(t('Media Spend Goal has to be a number'))
      .test('required-if-was-set', t('Media Spend Goal cannot be removed'), value => {
        const initialMediaSpendGoal = initialDealState?.pdp_config?.media_spend_goal;

        if (
          isEdit &&
          initialMediaSpendGoal !== null &&
          initialMediaSpendGoal !== undefined &&
          initialMediaSpendGoal !== '' &&
          value === null
        ) {
          return false;
        }

        return true;
      })
      .test('required-for-pdp-sync', t('Media Spend Goal is required'), function (value) {
        if (!options?.isCommitmentTermsEnabled) return true;
        return !(isPdpSyncDeal((this as TestOptions['options']).from) && value == null);
      })
      .test(
        'not-below-initial-media-spend-goal',
        t('You cannot set spend goals to be less than the amount already spent.'),
        function (value) {
          if (!options?.enforceMediaSpendGoalFloor || !isEdit) {
            return true;
          }

          if (!isPdpSyncDeal((this as TestOptions['options']).from)) {
            return true;
          }

          // Only enforce once the deal is actually PDP-synced and may have spend; a clone starts fresh with no sync.
          if (!initialDealState?.synchronized_with?.pdp_sync) {
            return true;
          }

          const initialMediaSpendGoal = initialDealState?.pdp_config?.media_spend_goal;

          if (initialMediaSpendGoal === null || initialMediaSpendGoal === undefined || value == null) {
            return true;
          }

          return Number(value) >= Number(initialMediaSpendGoal);
        }
      ),
    min_daily_avails: number()
      .nullable()
      .transform(emptyStringToNull)
      .typeError(t('Min Daily Avails has to be a number'))
      .integer(t('Min Daily Avails must be a whole number'))
      .min(1, t('Min Daily Avails must be at least 1'))
      .test('required-for-pdp-sync', t('Min Daily Avails is required'), function (value) {
        if (!options?.isCommitmentTermsEnabled) return true;
        return !(isPdpSyncDeal((this as TestOptions['options']).from) && value == null);
      }),
    min_win_rate: number()
      .nullable()
      .transform(emptyStringToNull)
      .typeError(t('Min Win Rate has to be a number'))
      .integer(t('Min Win Rate must be a whole number'))
      .min(1, t('Min Win Rate must be greater than 0%'))
      .max(100, t('Min Win Rate must be between 0 and 100%'))
      .test('required-for-pdp-sync', t('Min Win Rate is required'), function (value) {
        if (!options?.isCommitmentTermsEnabled) return true;
        return !(isPdpSyncDeal((this as TestOptions['options']).from) && value == null);
      }),
    period: number()
      .nullable()
      .transform(emptyStringToNull)
      .typeError(t('Period has to be a number'))
      .integer(t('Period must be a whole number'))
      .min(3, t('Period must be between 3 and 30 days'))
      .max(30, t('Period must be between 3 and 30 days'))
      .test('required-for-pdp-sync', t('Period is required'), function (value) {
        if (!options?.isCommitmentTermsEnabled) return true;
        return !(isPdpSyncDeal((this as TestOptions['options']).from) && value == null);
      }),
    threshold: number()
      .nullable()
      .transform(emptyStringToNull)
      .typeError(t('Threshold has to be a number'))
      .integer(t('Threshold must be a whole number'))
      .min(0, t('Threshold must be between 0 and 100%'))
      .max(100, t('Threshold must be between 0 and 100%'))
      .test('required-for-pdp-sync', t('Threshold is required'), function (value) {
        if (!options?.isCommitmentTermsEnabled) return true;
        return !(isPdpSyncDeal((this as TestOptions['options']).from) && value == null);
      }),
  })
    .nullable()
    .default(null),
  ...(options?.withAmazonBudget
    ? {
        [dealFormField.AMAZON_CONFIG]: object({
          budget: string()
            .nullable()
            .transform(emptyStringToNull)
            .test({
              name: 'validBudget',
              test(value) {
                if (value === null || value === undefined) return true;

                if (!AMAZON_BUDGET_NUMBER_REGEXP.test(value)) {
                  return this.createError({ message: t('Budget has to be a number') });
                }

                if (!FLOAT_NUMBER_WITH_MAX_THREE_DECIMALS.test(value)) {
                  return this.createError({ message: t('Budget can have up to 3 decimal places.') });
                }

                if (Number(value) < 1) {
                  return this.createError({ message: t('Budget has to be at least 1') });
                }

                return true;
              },
            }),
        })
          .nullable()
          .default(null)
          .test(
            'budget-required-for-amazon-pg',
            t('Budget is required for Amazon Programmatic Guaranteed deals'),
            function (value) {
              const parent = (this as TestOptions['options']).parent as Record<string, unknown>;
              const isGuaranteed = parent[dealFormField.PMP_DEAL_TYPE] === pmpDealTypes.Guaranteed;
              const participants =
                (parent[DealParticipantsField.DEAL_PARTICIPANTS] as
                  | Array<{ demand_partner?: string | null }>
                  | undefined) ?? [];
              const hasAmazonPartner = participants.some(
                participant => participant?.demand_partner === AMAZON_DEMAND_PARTNER_ID
              );

              if (!isGuaranteed || !hasAmazonPartner) {
                return true;
              }

              const budget = (value as { budget?: unknown } | null)?.budget;

              if (budget !== null && budget !== undefined && budget !== '') {
                return true;
              }

              // Anchor the error to the budget field so it renders on the input instead of the object.
              return this.createError({ path: `${this.path}.budget` });
            }
          ),
      }
    : {}),
  [dealFormField.START_DATE]: date()
    .nullable()
    .typeError(t('Enter date in format YYYY-MM-DD HH:MM AM'))
    .default(() => new Date())
    .test({
      name: 'start-date-validations',
      message: 'Invalid Start Date',
      test(value, ctx) {
        const initialStartDate = initialDealState?.start_date;
        const isPacingEnabled = this.parent[dealFormField.IS_PACING_ENABLED];
        const endDate = this.parent[dealFormField.END_DATE];
        const dealParticipants = this.parent[DealParticipantsField.DEAL_PARTICIPANTS];
        const now = new Date();
        const oneHourFromNow = new Date(now.getTime() + 60 * 60 * 1000);
        const twoYearsFromNow = new Date(new Date(now).setFullYear(now.getFullYear() + 2));

        if (!value) {
          return ctx.createError({ message: t('Start Date is required') });
        }

        const startDate = fromZonedTime(value, 'UTC');

        // [TTD] Validation: start date must be at least 1 hour in the future
        const hasTTD = dealParticipants?.some(
          (participant: DealParticipant) => participant?.demand_partner === TTD_DEMAND_PARTNER_ID
        );
        const shouldEnforceTTDValidation = !isEdit || !this.parent.old_ttd_sync_allowed;

        if (hasTTD && shouldEnforceTTDValidation) {
          // Skip "start date must be in the future" validation for edit mode
          if (!isEdit && oneHourFromNow >= startDate) {
            return ctx.createError({
              message: t('TTD deal start date must be at least 1h from now.'),
            });
          }

          // [TTD] Validation: start date cannot exceed 2 years from today
          const startDateExceedsTwoYears = startDate > twoYearsFromNow;

          if (startDateExceedsTwoYears) {
            return ctx.createError({
              message: t('TTD deal start date cannot exceed 2 years from today.'),
            });
          }
        }

        if (isPacingEnabled) {
          if (initialStartDate) {
            const initStart = fromZonedTime(initialStartDate, 'UTC');
            const isStartDateOneHourAhead = initStart >= oneHourFromNow;

            if (isEdit && !isStartDateOneHourAhead) {
              // Do not validate start date if it is not one hour ahead, because it is not editable
              // Reference: PacingFormDate.tsx - isStartDateDisabled
              return true;
            }
          }

          if (oneHourFromNow >= startDate) {
            return ctx.createError({
              message: t('Start Date must be at least 1 hour from now.'),
            });
          }

          const isBigger1hThanNow = new Date(startDate.getTime() + 60 * 60 * 1000) > fromZonedTime(endDate, 'UTC');

          if (endDate && isBigger1hThanNow) {
            return ctx.createError({
              message: t('Start Date must be at least 1h earlier than End Date.'),
            });
          }
        }

        const startEqualOrSameThanNow = startDate >= fromZonedTime(endDate, 'UTC');

        if (endDate && startEqualOrSameThanNow) {
          return ctx.createError({
            message: t('Start Date must be earlier than End Date.'),
          });
        }

        return true;
      },
    }),
  [dealFormField.END_DATE]: date()
    .nullable()
    .typeError(t('Enter date in format YYYY-MM-DD HH:MM AM'))
    .test({
      name: 'end-date-validations',
      message: 'Invalid End Date',
      test(value, ctx) {
        const isPacingEnabled = this.parent[dealFormField.IS_PACING_ENABLED];
        const pmpDealType = this.parent[dealFormField.PMP_DEAL_TYPE];
        const dealSource = this.parent[dealFormField.DEAL_SOURCE];
        const pacingMode = this.parent[dealFormField.PACING_MODE];
        const budgetMode = this.parent[dealFormField.BUDGET_MODE];
        const demandPartner = this.parent[dealFormField.DEAL_PARTICIPANTS]?.[0]?.demand_partner;
        const dealParticipants = this.parent[DealParticipantsField.DEAL_PARTICIPANTS];

        const hasDV360DemandPartner = this.parent[dealFormField.DEAL_PARTICIPANTS]?.some(
          item => item.demand_partner === DV360_DEMAND_PARTNER_ID
        );

        const endDateRequiredForPacing =
          pacingMode === PACING_MODE.EVENLY &&
          (pmpDealType === pmpDealTypes.Guaranteed || budgetMode === BUDGET_MODE.CAMPAIGN_FLIGHT_TIME);

        // [TTD] Validation: end date is mandatory
        const hasTTD = dealParticipants?.some(
          (participant: DealParticipant) => participant?.demand_partner === TTD_DEMAND_PARTNER_ID
        );
        const shouldEnforceTTDValidation = !isEdit || !this.parent.old_ttd_sync_allowed;

        if (hasTTD && shouldEnforceTTDValidation && !value) {
          return ctx.createError({
            message: t('TTD deal end date is required.'),
            path: ctx.path,
          });
        }

        if (hasDV360DemandPartner && dealSource !== DEAL_SOURCE_DSP_SOURCED && !value) {
          return ctx.createError({
            message: t('End date is required'),
            path: ctx.path,
          });
        }

        if (isPacingEnabled) {
          if (endDateRequiredForPacing) {
            if (!value) {
              return ctx.createError({
                message: t('End date is required'),
                path: ctx.path,
              });
            }
          }
        } else {
          if (pmpDealType === pmpDealTypes.Guaranteed && demandPartner === TTD_DEMAND_PARTNER_ID) {
            if (!value) {
              return ctx.createError({
                message: t('End date is required'),
                path: ctx.path,
              });
            }
          }
        }

        // [TTD] Validation: end date maximum 2 years from today
        if (value) {
          const dealParticipants = this.parent[DealParticipantsField.DEAL_PARTICIPANTS];
          const hasTTD = dealParticipants?.some(
            (participant: DealParticipant) => participant.demand_partner === TTD_DEMAND_PARTNER_ID
          );
          const shouldEnforceTTDValidation = !isEdit || !this.parent.old_ttd_sync_allowed;

          if (hasTTD && shouldEnforceTTDValidation) {
            const endDate = fromZonedTime(value, 'UTC');
            const now = new Date();
            const twoYearsFromNow = new Date(new Date(now).setFullYear(now.getFullYear() + 2));
            const endDateExceedsTwoYears = endDate > twoYearsFromNow;

            if (endDateExceedsTwoYears) {
              return ctx.createError({
                message: t('TTD deal end date cannot exceed 2 years from today.'),
                path: ctx.path,
              });
            }
          }
        }

        return true;
      },
    }),

  [dealFormField.DEAL_SOURCE]: string().nullable().required(t('Deal Source is required')),
  [dealFormField.PACKAGE_UID]: packageValidation(),
  [dealFormField.RBO_CONFIG]: lazy(value => {
    if (value && (!value.mode || value.mode === rboConfigMode.CUSTOM)) {
      return object().shape({
        [rboConfigField.MODE]: string().transform(emptyStringToNull).nullable(),
        [rboConfigField.CUSTOM_SCORE_NAME]: string()
          .transform(emptyStringToNull)
          .nullable()
          .test(
            'customScoreNameValidationDynamicConversion',
            t('Custom Score Name is required for Dynamic Conversion deals'),
            function (customScoreName) {
              const { tactic, goal } = this.parent;

              // Required: if tactic === DYNAMIC and goal === CONVERSION
              if (tactic === rboConfigTactic.DYNAMIC && goal === rboConfigModelGoal.CONVERSION) {
                return !!customScoreName; // must not be null/empty
              }

              // if no customScoreName and not required
              return true;
            }
          )
          .test(
            'customScoreNameFixedThreshold',
            t(
              'Custom Score Name requires Fixed Tactic and Threshold to be filled (or) Dynamic Tactic with Conversion Model Goal'
            ),
            function (customScoreName) {
              const { threshold, tactic, goal } = this.parent;

              // Optional validation: if field is filled
              if (customScoreName) {
                return (
                  (tactic === rboConfigTactic.FIXED && threshold != null) ||
                  (tactic === rboConfigTactic.DYNAMIC && goal === rboConfigModelGoal.CONVERSION)
                );
              }

              // if no customScoreName and not required
              return true;
            }
          ),
        [rboConfigField.HOURLY_TARGET]: number()
          .transform(emptyStringToNull)
          .nullable()
          .typeError(t('Request Goal has to be a number'))
          .positive(t('Request Goal has to be bigger than 0'))
          .integer(t('Request Goal cannot be a decimal number'))
          .when({
            is: () => value.tactic === rboConfigTactic.DYNAMIC && value.goal === rboConfigModelGoal.CONVERSION,
            then: schema => schema.required(t('Request Goal is required for Dynamic Conversion deal')),
            otherwise: schema => schema.notRequired(),
          }),
        [rboConfigField.CHANNEL]: string()
          .transform(emptyStringToNull)
          .nullable()
          .when({
            is: () => value.goal === rboConfigModelGoal.CONVERSION,
            then: schema => schema.notRequired(),
            otherwise: schema => schema.required(t('Channel is required')),
          }),
        [rboConfigField.MODEL_GOAL]: string()
          .transform(emptyStringToNull)
          .nullable()
          .when({
            is: () => value.mode === rboConfigMode.CUSTOM,
            then: schema => schema.required(t('Goal is required')),
            otherwise: schema => schema.notRequired(),
          }),
        [rboConfigField.TARGET]: string().transform(emptyStringToNull).nullable(),
        [rboConfigField.DROP_PERCENT]: string()
          .transform(emptyStringToNull)
          .nullable()
          .notRequired()
          .matches(/^-?\d+$/, t('Traffic Drop cannot be a decimal number'))
          .test(
            'range',
            t('Traffic Drop must be between 0 and 100'),
            value => value == null || (Number(value) >= 0 && Number(value) <= 100)
          ),
        [rboConfigField.TACTIC]: string().transform(emptyStringToNull).nullable(),
        [rboConfigField.THRESHOLD]: number()
          .transform(emptyStringToNull)
          .nullable()
          .when({
            is: () =>
              value.tactic === rboConfigTactic.FIXED ||
              (value.tactic === rboConfigTactic.FIXED && value.customScoreName === rboConfigField.CUSTOM_SCORE_NAME),
            then: schema => schema.required(t('Threshold is required')),
            otherwise: schema => schema.notRequired(),
          })
          .typeError(t('Threshold has to be a number'))
          .min(0, t('Threshold must be greater than or equal to 0'))
          .max(1, t('Threshold can not exceed max value: 1'))
          .test('maxDigitsAfterDecimal', t('Threshold can have up to 5 decimal places'), number => {
            return !number || (number.toString().match(/^\d+(\.\d{0,5})?$/i) || []).length > 0;
          }),
      });
    }
    if (value && (value.mode === 'boost' || value.mode === 'max')) {
      return object().shape({
        [rboConfigField.MODE]: string().transform(emptyStringToNull).nullable().required(t('Mode is required')),
      });
    }
    return mixed().nullable().notRequired();
  }),
  [dealFormField.SEGMENT_PARTNER_REVENUE_METHOD]: string().when(dealFormField.HAS_MULTIPROVIDER_AUDIENCE, {
    is: hasMultiProvider => hasMultiProvider,
    then: schema => schema.nullable().required(t('Fee Type is required')),
    otherwise: schema => schema.nullable().default(null).notRequired(),
  }),
  //makes that validation needs to lose focus on deal pmp type
  [dealFormField.OPEN_AUCTION_ACCESS]: string()
    .nullable()
    .when(dealFormField.PMP_DEAL_TYPE, ([dealType], schema) => {
      return schema.test({
        message: t('Field is required'),
        test: value => {
          if (dealType !== pmpDealTypes.PreferredDeal && dealType !== pmpDealTypes.PrivateAuction) {
            return true;
          }

          return Boolean(value);
        },
      });
    }),
  [dealFormField.DEAL_ID]: isEdit
    ? string()
        .trim(t('Deal ID contains some leading or trailing spaces. Please remove them.'))
        .strict(true)
        .required(t('Deal ID is required'))
    : string().notRequired(),
  [dealFormField.IMPRESSION_GOAL]: number()
    .nullable()
    .when([dealFormField.PMP_DEAL_TYPE, dealFormField.IS_PACING_ENABLED], {
      is: (dealType, isPacingEnabled) => dealType === pmpDealTypes.Guaranteed && !isPacingEnabled,
      then: schema =>
        schema
          .transform(emptyStringToUndefined)
          .typeError(t('Impression Goal has to be a number'))
          .max(
            MAX_SAFE_INTEGER_ROUNDED,
            t('Impression Goal has to be smaller than {max}', { max: MAX_SAFE_INTEGER_ROUNDED })
          )
          .test('integer', t('Impression Goal has to be integer'), value => !value || value === Math.floor(value))
          .positive(t('Number of impressions must be bigger than 0'))
          .required(t('Impression Goal is required')),
      otherwise: schema => schema.notRequired(),
    }),

  [dealFormField.CUSTOM_DEAL_ID]: isEdit
    ? string().optional()
    : string()
        .nullable()
        .when(dealFormField.DEAL_ID_TYPE_RADIO, {
          is: radioType => radioType === 'CUSTOM',
          then: schema =>
            schema
              .trim(t('Deal ID contains some leading or trailing spaces. Please remove them.'))
              .strict(true)
              .required(t('Deal ID is required'))
              .matches(/^[a-zA-Z0-9_-]+$/, t('Deal ID must consist of letters, numbers, dashes, and underscores only')),
          otherwise: schema => schema.optional(),
        }),
  [dealFormField.EXTERNAL_DEAL_ID]: isEdit
    ? string().optional()
    : string()
        .nullable()
        .when(dealFormField.DEAL_ID_TYPE_RADIO, {
          is: radioType => radioType === 'EXTERNAL',
          then: schema =>
            schema
              .trim(t('Deal ID contains some leading or trailing spaces. Please remove them.'))
              .strict(true)
              .required(t('Deal ID is required'))
              .matches(/^[a-zA-Z0-9_-]+$/, t('Deal ID must consist of letters, numbers, dashes, and underscores only'))
              .min(3, t('Deal ID must be at least 3 characters long')),
          otherwise: schema => schema.optional(),
        }),
  [dealFormField.BUYER_ACCOUNT_ID]: string()
    .nullable()
    .when(dealFormField.PMP_DEAL_TYPE, {
      is: pmpDealTypes.PrivateAuction,
      then: schema => schema.optional(),
      otherwise: schema => schema.strip(),
    }),
  [dealFormField.IS_PACING_ENABLED]: boolean(),
  [dealFormField.MONETIZING_BUDGET]: number()
    .nullable()
    .transform(emptyStringToNull)
    .when(dealFormField.BUDGET_TYPE, {
      is: BUDGET_TYPE.SPEND,
      then: schema =>
        schema
          .typeError(t('Budget has to be a number'))
          .positive(t('Budget has to be bigger than 0'))
          .max(MAX_MONETIZING_BUDGET, t('Budget has to be smaller than {max}', { max: MAX_MONETIZING_BUDGET }))
          .test('maxDigitsAfterDecimal', t('Budget can have up to 2 decimal places.'), number => {
            return !number || (number.toString().match(/^\d+(\.\d{0,2})?$/i) || []).length > 0;
          })
          .when(dealFormField.IS_PACING_ENABLED, {
            is: true,
            then: schema =>
              schema
                .nullable()
                .transform(emptyStringToNull)
                .typeError(t('Budget has to be a number'))
                .required(t('Budget is required')),
          }),
    })
    .when(dealFormField.BUDGET_TYPE, {
      is: BUDGET_TYPE.IMPRESSIONS,
      then: schema =>
        schema
          .typeError(t('Impression Goal has to be a number'))
          .positive(t('Impression Goal has to be bigger than 0'))
          .test(
            'max',
            t('Impression Goal has to be smaller than {max}', { max: MAX_MONETIZING_IMPRESSIONS }),
            isWithinBigIntMax(MAX_MONETIZING_IMPRESSIONS)
          )
          .integer(t('Impression Goal must be an integer'))
          .when(dealFormField.IS_PACING_ENABLED, {
            is: true,
            then: schema =>
              schema
                .transform(emptyStringToNull)
                .nullable()
                .typeError(t('Impression Goal as to be a number'))
                .required(t('Impression Goal is required')),
          }),
    }),
  [dealFormField.PACING_MODE]: string()
    .nullable()
    .when(dealFormField.IS_PACING_ENABLED, {
      is: true,
      then: schema => schema.typeError(t('Monetizing Mode is required')).required(t('Monetizing Mode is required')),
    }),
  [dealFormField.MONETIZING_REGION]: string()
    .nullable()
    .when(dealFormField.IS_PACING_ENABLED, {
      is: true,
      then: schema => schema.typeError(t('Region is required')).required(t('Region is required')),
    }),
  [dealFormField.MONETIZING_CADENCE_UNIT]: string()
    .nullable()
    .when(dealFormField.BUDGET_MODE, {
      is: mode => mode === BUDGET_MODE.CADENCE,
      then: schema => schema.typeError(t('Cadence Type is required')).required(t('Cadence Type is required')),
    }),
  [dealFormField.FREQUENCY_CAPPING]: getFrequencyCappingValidationSchema(t),
});

export const getDealValidationSchema = (
  t: I18nTranslate,
  isEdit: boolean,
  initialDealState?: DealState,
  options?: {
    enforceMediaSpendGoalFloor?: boolean;
    isCommitmentTermsEnabled?: boolean;
    withAmazonBudget?: boolean;
  }
) => {
  return object().shape(getDealValidation(t, isEdit, initialDealState, options));
};
